import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Avatar from "@/components/base/Avatar";
import type { RpsBrief } from "@/lib/types";
import {
  STATE_BADGE,
  STATE_BORDER,
  STATE_STRIP,
  formatCountdown,
  handFull,
  isDim,
  stateLabel,
} from "../utils";

interface Props {
  brief: RpsBrief;
  /** server_ts 기준 경과 초 (PC 시계를 믿지 않는다) */
  elapsed?: number;
}

// 목록 · 메인 공통 가위바위보 카드.
// state === "open" 이면 서버가 host_hand 를 빈 값으로 주므로 «🔒 마감 후 공개» 로만 표시한다.
export default function RpsCard({ brief, elapsed = 0 }: Props) {
  const [item, setItem] = useState<RpsBrief>(brief);

  useEffect(() => {
    setItem(brief);
  }, [brief]);

  const open = item.state === "open";
  const remaining = Math.max(0, (item.end_in ?? 0) - elapsed);
  const cd = formatCountdown(remaining);
  const unlimited = item.limit_cnt === 0;
  const left = item.left_cnt;
  const dim = isDim(item.state);
  const mine = item.is_mine === 1;
  const joined = item.is_joined === 1;

  return (
    <article
      id={`rps-${item.no}`}
      className={`relative flex h-full flex-col overflow-hidden rounded-xl border bg-background-50 ${
        STATE_BORDER[item.state]
      } ${dim ? "opacity-70" : ""}`}
    >
      <span
        className={`absolute inset-y-0 left-0 w-1.5 ${STATE_STRIP[item.state]}`}
        aria-hidden="true"
      ></span>

      <div className="flex flex-1 flex-col p-5 pl-6">
        {/* 상태 뱃지 */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              STATE_BADGE[item.state]
            }`}
          >
            {stateLabel(item.state, item.state_txt)}
          </span>
          {mine && (
            <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
              내가 연 판
            </span>
          )}
          {joined && !mine && (
            <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
              참여완료
            </span>
          )}
        </div>

        {/* 제목 */}
        <h3 className="mt-3 flex items-start gap-2 font-heading text-base font-bold leading-snug text-foreground-950">
          <span aria-hidden="true">✊</span>
          <Link to={`/rps/${item.no}`} className="hover:text-primary-700">
            {item.subject}
          </Link>
        </h3>

        {/* 등록자 */}
        <div className="mt-2.5 flex items-center gap-2 text-sm text-foreground-600">
          <Avatar name={item.host?.name} photo={item.host?.photo} size={28} />
          <span>
            <strong className="font-semibold text-foreground-800">
              {item.host?.aho} {item.host?.name}
            </strong>{" "}
            님이 냈습니다
          </span>
        </div>

        {/* 손 공개 여부 */}
        <div className="mt-3">
          {open ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-background-100 px-3 py-2 text-sm font-semibold text-foreground-600">
              <i className="ri-lock-2-line text-foreground-400" aria-hidden="true"></i>
              손은 마감 후 공개
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-lg bg-background-100 px-3 py-2 text-sm text-foreground-600">
              등록자
              <strong className="font-heading font-bold text-foreground-950">
                {handFull(item.host_hand, item.host_hand_txt)}
              </strong>
            </span>
          )}
        </div>

        {/* 보상 */}
        {item.has_reward === 1 && item.reward ? (
          <p className="mt-2.5 inline-flex items-center gap-1.5 text-sm text-foreground-700">
            <span aria-hidden="true">🎁</span>
            이긴 사람 : <strong className="font-semibold">{item.reward}</strong>
          </p>
        ) : null}

        {/* 카운트다운 · 인원 */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          {open ? (
            <span className="inline-flex items-center gap-1.5 text-foreground-600">
              <i
                className="ri-alarm-warning-line text-foreground-400"
                aria-hidden="true"
              ></i>
              마감까지{" "}
              <strong
                className={`tabular-nums ${
                  cd.urgent ? "animate-pulse text-accent-700" : "text-foreground-900"
                }`}
              >
                {cd.text}
              </strong>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-foreground-600">
              <i className="ri-flag-line text-foreground-400" aria-hidden="true"></i>
              이긴 사람{" "}
              <strong className="text-foreground-900">{item.win_cnt ?? 0}명</strong>
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-foreground-600">
            <i className="ri-group-line text-foreground-400" aria-hidden="true"></i>
            {unlimited ? (
              <>
                <strong className="text-foreground-900">{item.join_cnt}</strong>명 ·
                인원 제한 없음
              </>
            ) : (
              <>
                <strong className="text-foreground-900">{item.join_cnt}</strong> /{" "}
                {item.limit_cnt}명
              </>
            )}
          </span>
          {open && !unlimited && left >= 1 && left <= 2 && (
            <span className="font-bold text-accent-700">{left}자리 남음</span>
          )}
        </div>

        {/* CTA */}
        <div className="mt-4 flex flex-1 items-end">
          <Link
            to={`/rps/${item.no}`}
            className={`inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-bold transition-colors ${
              open
                ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
            }`}
          >
            {open ? (mine ? "내 판 관리" : joined ? "결과 기다리기" : "참여하기") : "결과 보기"}
            <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}